import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTenant, updateTenant, type Tenant, type TenantPatch } from '../api/tenants.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';
import { DEFAULT_APP_TIME_ZONE, TIME_ZONE_OPTIONS } from '@zengine/tz';

type FormTier = Tenant['tier'] | 'enterprise';
const LOCALES = ['es-CL', 'es', 'en', 'en-US', 'pt-BR'];
const FLAG_KEY_REGEX = /^[a-z0-9][a-z0-9_-]*$/i;

export function TenantEdit(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();

  const tenantQuery = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => getTenant(id),
    enabled: Boolean(id),
  });

  const initial = useMemo(() => {
    const tn = tenantQuery.data;
    return {
      name: tn?.name ?? '',
      tier: (tn?.tier ?? 'logical') as FormTier,
      status: tn?.status ?? 'active',
      defaultLocale: tn?.defaultLocale ?? 'es-CL',
      appTimeZone: tn?.appTimeZone ?? DEFAULT_APP_TIME_ZONE,
      flags: tn?.featureFlags ?? {},
    };
  }, [tenantQuery.data]);

  const [name, setName] = useState(initial.name);
  const [tier, setTier] = useState<FormTier>(initial.tier);
  const [status, setStatus] = useState<NonNullable<Tenant['status']>>(initial.status);
  const [locale, setLocale] = useState(initial.defaultLocale);
  const [timeZone, setTimeZone] = useState(initial.appTimeZone);
  const [flags, setFlags] = useState<Record<string, boolean>>(initial.flags);
  const [newKey, setNewKey] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    setName(initial.name);
    setTier(initial.tier);
    setStatus(initial.status);
    setLocale(initial.defaultLocale);
    setTimeZone(initial.appTimeZone);
    setFlags(initial.flags);
  }, [initial]);

  const mutation = useMutation({
    mutationFn: (patch: TenantPatch) => {
      const tn = tenantQuery.data;
      if (!tn) throw new Error('tenant not loaded');
      return updateTenant(tn.id, patch, tn.version ?? 1);
    },
    onSuccess: () => {
      pushToast('success', t.tenant.updated);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      navigate(`/tenants/${id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 412) {
        pushToast('error', t.tenant.versionConflict);
        setErrMsg(t.tenant.versionConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
      setErrMsg(msg);
    },
  });

  function addFlag(): void {
    const k = newKey.trim();
    if (!k) return;
    if (!FLAG_KEY_REGEX.test(k)) {
      setErrMsg(t.tenant.flagKeyInvalid);
      return;
    }
    setFlags((prev) => ({ ...prev, [k]: false }));
    setNewKey('');
    setErrMsg(null);
  }

  function toggleFlag(k: string): void {
    setFlags((prev) => ({ ...prev, [k]: !prev[k] }));
  }

  function removeFlag(k: string): void {
    setFlags((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
  }

  function onSubmit(ev: React.FormEvent): void {
    ev.preventDefault();
    setErrMsg(null);
    if (tier === 'enterprise') {
      setErrMsg(t.tenant.enterpriseDisabled);
      return;
    }
    if (!name.trim()) {
      setErrMsg(t.tenant.nameRequired);
      return;
    }
    const tn = tenantQuery.data;
    if (!tn) return;
    const patch: TenantPatch = {};
    if (name.trim() !== tn.name) patch.name = name.trim();
    if (tier !== tn.tier) patch.tier = tier as Tenant['tier'];
    if (status !== tn.status) patch.status = status;
    if (locale !== tn.defaultLocale) patch.defaultLocale = locale;
    if (timeZone !== (tn.appTimeZone ?? DEFAULT_APP_TIME_ZONE)) patch.appTimeZone = timeZone;
    if (JSON.stringify(flags) !== JSON.stringify(tn.featureFlags ?? {})) {
      patch.featureFlags = flags;
    }
    if (Object.keys(patch).length === 0) {
      pushToast('success', t.tenant.noChanges);
      navigate(`/tenants/${id}`);
      return;
    }
    mutation.mutate(patch);
  }

  if (tenantQuery.isLoading) return <p>{t.app.loading}</p>;
  if (tenantQuery.error) {
    return <p className="text-red-700">{(tenantQuery.error as Error).message}</p>;
  }
  if (!tenantQuery.data) return <p>{t.tenant.empty}</p>;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">{t.tenant.editTitle}</h2>
      <form onSubmit={onSubmit} className="space-y-4 rounded border border-slate-200 bg-white p-5" noValidate>
        <Field label={t.tenant.slug} helper={t.tenant.slugReadonly}>
          <input
            type="text"
            value={tenantQuery.data.slug}
            disabled
            className="w-full rounded border border-slate-300 bg-slate-100 px-2 py-1 font-mono text-sm"
          />
        </Field>

        <Field label={t.tenant.name}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            required
          />
        </Field>

        <Field label={t.tenant.tier} helper={t.tenant.tierHelper}>
          <div className="grid grid-cols-3 gap-2">
            {(['logical', 'physical', 'enterprise'] as FormTier[]).map((tk) => {
              const isEnt = tk === 'enterprise';
              return (
                <label
                  key={tk}
                  className={`flex cursor-pointer items-center gap-2 rounded border p-2 ${
                    tier === tk ? 'border-brand-500 bg-brand-50' : 'border-slate-200'
                  } ${isEnt ? 'opacity-60' : ''}`}
                  title={isEnt ? t.tenant.enterpriseSoon : undefined}
                >
                  <input
                    type="radio"
                    name="tier"
                    value={tk}
                    checked={tier === tk}
                    onChange={() => setTier(tk)}
                    disabled={isEnt}
                  />
                  <span className="text-sm font-medium">{t.tenant.tierLabels[tk]}</span>
                  {isEnt && (
                    <span className="ml-auto rounded bg-amber-200 px-2 py-0.5 text-[10px] text-amber-900">
                      {t.tenant.enterpriseSoon}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </Field>

        <Field label="Estado">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as NonNullable<Tenant['status']>)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="suspended">suspended</option>
          </select>
        </Field>

        <Field label={t.tenant.locale}>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Zona horaria" helper="Usada por reportes y dashboards para mostrar timestamps al usuario final.">
          <select
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {TIME_ZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t.tenant.featureFlags} helper={t.tenant.flagsHelper}>
          <div className="space-y-2">
            {Object.keys(flags).length === 0 && (
              <p className="text-xs text-slate-400">{t.tenant.flagsEmpty}</p>
            )}
            {Object.entries(flags)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 rounded border border-slate-200 p-2 text-sm">
                  <span className="flex-1 font-mono">{k}</span>
                  <button
                    type="button"
                    onClick={() => toggleFlag(k)}
                    className={`rounded px-3 py-1 text-xs ${
                      v
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {v ? 'on' : 'off'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFlag(k)}
                    className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                    aria-label={`remove ${k}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            <div className="flex gap-2">
              <input
                type="text"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addFlag();
                  }
                }}
                placeholder={t.tenant.flagKeyPlaceholder}
                className="flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
              <button
                type="button"
                onClick={addFlag}
                className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-50"
              >
                {t.tenant.addFlag}
              </button>
            </div>
          </div>
        </Field>

        {errMsg && (
          <p role="alert" className="text-xs text-red-700">
            {errMsg}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Link
            to={`/tenants/${id}`}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.app.cancel}
          </Link>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {mutation.isPending ? '…' : t.app.save}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && <span className="mt-1 block text-xs text-slate-500">{helper}</span>}
    </label>
  );
}
